/**
 * listPanelStyles.js
 *
 * Shared visual tokens and style objects for the Survey Explorer Tools panels.
 * Modeled on Console's RecipientBrowser, the project's canonical list pattern
 * (see /Users/foodlebump/samsur/console/src/components/RecipientBrowser.js
 * and design tokens in /Users/foodlebump/samsur/UI artifacts/design-tokens.css).
 *
 * Previously each Tools panel carried its own ad-hoc inline styles with
 * diverging colors, paddings, and button shapes. Importing from here keeps
 * the active Tools panels visually aligned and gives us one place to
 * adjust the entire surface when tokens change.
 *
 * Usage:
 *   import { tokens, panelStyles } from './listPanelStyles';
 *   ...
 *   <div style={panelStyles.header}>...</div>
 *   <button style={panelStyles.btnPrimary}>...</button>
 */

export const tokens = {
    // Brand
    primary: '#5B9FFF',
    primaryHover: '#3D85E6',
    primaryDeep: '#2B6BBF',
    primaryFaint: '#EBF4FF',
    primaryFaintHover: '#D1E7FF',

    // Surfaces
    panelBg: '#FFFFFF',
    pageBg: 'var(--color-bg-base)',
    headerBg: '#FFFFFF',
    // Selection/action band. The design system has no dark-slate surface, so
    // this is the same quiet neutral used by the panel footer.
    batchBarBg: '#FAFBFC',

    // Borders
    borderLight: '#E8EAED',
    borderMedium: '#DADCE0',
    borderRowDivider: '#F5F6F7',

    // Text
    textPrimary: '#202124',
    textSecondary: '#5F6368',
    textTertiary: '#888888',
    textDisabled: '#BDC1C6',
    textInverse: '#FFFFFF',

    // Semantic
    error: '#F44336',
    errorBg: '#FCE8E6',
    errorText: '#991b1b',
    success: '#4CAF50',
    successBg: '#E8F5E9',
    warning: '#FFC107',

    // Type
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSizeXs: 11,
    fontSizeSm: 13,
    fontSizeMd: 14,
    fontSizeLg: 16,
    fontSizeXl: 18,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,

    // Spacing
    spaceXs: 4,
    spaceSm: 8,
    spaceMd: 12,
    spaceBase: 16,
    spaceLg: 20,
    spaceXl: 24,
    space2xl: 32,

    // Radius
    radiusSm: 4,
    radiusMd: 6,
    radiusLg: 8,
    radiusPill: 12,
};

const t = tokens;

export const panelStyles = {
    // Outermost wrapper inside the dockable shell.
    shell: {
        display: 'flex', flexDirection: 'column', height: '100%',
        background: t.panelBg, fontFamily: t.fontFamily, color: t.textPrimary,
    },

    // Header bar (title + global actions).
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: `${t.spaceBase}px ${t.spaceLg}px`,
        borderBottom: `1px solid ${t.borderLight}`,
        background: t.headerBg,
    },
    headerTitle: {
        margin: 0, fontSize: t.fontSizeXl,
        fontWeight: t.fontWeightSemibold, color: t.textPrimary,
    },
    headerSubtitle: {
        marginTop: 4, fontSize: t.fontSizeSm, color: t.textSecondary,
    },
    badge: {
        background: t.primaryFaint, color: t.primary,
        padding: '2px 10px', borderRadius: t.radiusPill,
        fontSize: t.fontSizeSm, fontWeight: t.fontWeightMedium,
        display: 'inline-block',
    },

    // Filter / command bar (search + selects + inline actions).
    filterBar: {
        display: 'flex', gap: t.spaceSm,
        padding: `${t.spaceMd}px ${t.spaceLg}px`,
        borderBottom: `1px solid ${t.borderRowDivider}`,
        flexWrap: 'wrap', alignItems: 'center',
        background: t.panelBg,
    },
    searchInput: {
        flex: 1, minWidth: 180,
        padding: '6px 10px', borderRadius: t.radiusMd,
        border: `1px solid ${t.borderMedium}`,
        fontSize: t.fontSizeSm, outline: 'none', background: t.panelBg,
    },
    select: {
        padding: '6px 8px', borderRadius: t.radiusMd,
        border: `1px solid ${t.borderMedium}`,
        fontSize: t.fontSizeSm, background: t.panelBg, cursor: 'pointer',
    },
    textInput: {
        padding: '6px 10px', borderRadius: t.radiusMd,
        border: `1px solid ${t.borderMedium}`,
        fontSize: t.fontSizeSm, outline: 'none', background: t.panelBg,
    },
    checkboxLabel: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: t.fontSizeSm, color: t.textSecondary, cursor: 'pointer',
    },
    fieldLabel: {
        fontSize: t.fontSizeXs, fontWeight: t.fontWeightSemibold,
        color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px',
    },

    // Buttons. btnPrimary is the brand blue; btnSecondary is a neutral outline.
    // btnDanger is reserved for destructive actions (delete, purge).
    btnPrimary: {
        padding: '6px 16px', borderRadius: t.radiusMd, border: 'none',
        background: t.primary, color: t.textInverse,
        fontSize: t.fontSizeSm, fontWeight: t.fontWeightMedium, cursor: 'pointer',
    },
    btnSecondary: {
        padding: '6px 14px', borderRadius: t.radiusMd,
        border: `1px solid ${t.borderMedium}`,
        background: t.panelBg, color: t.textPrimary,
        fontSize: t.fontSizeSm, fontWeight: t.fontWeightMedium, cursor: 'pointer',
    },
    btnDanger: {
        padding: '6px 14px', borderRadius: t.radiusMd, border: 'none',
        background: t.error, color: t.textInverse,
        fontSize: t.fontSizeSm, fontWeight: t.fontWeightMedium, cursor: 'pointer',
    },
    btnDisabled: {
        opacity: 0.5, cursor: 'not-allowed',
    },
    btnSmall: {
        padding: '4px 10px', fontSize: t.fontSizeXs + 1,
    },
    actionBtn: {
        padding: '4px 12px', borderRadius: t.radiusMd,
        border: `1px solid ${t.borderMedium}`,
        fontSize: t.fontSizeXs + 1, cursor: 'pointer',
        fontWeight: t.fontWeightMedium, background: t.panelBg,
        display: 'inline-flex', alignItems: 'center', gap: 4,
    },

    // Tables: container scrolls; header sticks; rows divide softly.
    tableContainer: {
        flex: 1, overflow: 'auto', padding: `0 ${t.spaceLg}px`,
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: t.fontSizeSm,
    },
    th: {
        padding: '8px 6px', textAlign: 'left',
        borderBottom: `2px solid ${t.borderLight}`,
        fontWeight: t.fontWeightSemibold, fontSize: t.fontSizeXs, color: t.textPrimary,
        position: 'sticky', top: 0, background: t.panelBg,
        userSelect: 'none', whiteSpace: 'nowrap',
    },
    thSortable: {
        cursor: 'pointer',
    },
    td: {
        padding: '6px', borderBottom: `1px solid ${t.borderRowDivider}`,
        whiteSpace: 'nowrap', verticalAlign: 'middle',
    },
    row: {
        transition: 'background 0.1s',
    },
    rowSelected: {
        background: t.primaryFaint,
    },

    // Standard list-empty placeholder.
    emptyState: {
        padding: t.spaceXl, textAlign: 'center',
        color: t.textDisabled, fontSize: t.fontSizeSm,
    },

    // Inline error bar (matches RecipientBrowser pattern). Sits below the filter row.
    errorBar: {
        padding: `${t.spaceSm}px ${t.spaceLg}px`,
        background: t.errorBg, color: t.errorText,
        fontSize: t.fontSizeSm,
        borderBottom: `1px solid ${t.error}`,
    },

    // Loading bar — same vertical real estate as errorBar, neutral palette.
    loadingBar: {
        padding: `${t.spaceSm}px ${t.spaceLg}px`,
        color: t.textSecondary, fontSize: t.fontSizeSm,
        borderBottom: `1px solid ${t.borderRowDivider}`,
    },

    // Selection/action row. It sits above the list body and carries the
    // selection count plus the bulk actions for the whole list. Per
    // "UI artifacts/list-management.md" §Required structure, bulk actions live
    // here — never repeated on each row.
    batchBar: {
        display: 'flex', alignItems: 'center', gap: t.spaceMd,
        padding: `${t.spaceSm}px ${t.spaceLg}px`,
        background: t.batchBarBg, color: t.textSecondary,
        fontSize: t.fontSizeSm,
        borderBottom: `1px solid ${t.borderLight}`,
    },
    batchBarBtn: {
        padding: '4px 12px', borderRadius: t.radiusMd,
        border: `1px solid ${t.borderMedium}`,
        background: t.panelBg, color: t.textPrimary,
        fontSize: t.fontSizeSm, fontWeight: t.fontWeightMedium, cursor: 'pointer',
    },
    batchBarBtnDanger: {
        padding: '4px 12px', borderRadius: t.radiusMd,
        border: '1px solid #EF4444',
        background: '#FEF2F2', color: '#DC2626',
        fontSize: t.fontSizeSm, fontWeight: t.fontWeightMedium, cursor: 'pointer',
    },
    batchBarBtnDisabled: {
        border: `1px solid ${t.borderLight}`,
        background: t.borderRowDivider, color: t.textDisabled,
        cursor: 'not-allowed',
    },
    batchBarLabel: {
        marginRight: 'auto', fontSize: t.fontSizeSm,
        fontWeight: t.fontWeightMedium, color: t.textSecondary,
    },

    // Subsection within a panel — used by Cohort Builder for its
    // available-tags / cohort-list bands.
    section: {
        padding: `${t.spaceMd}px ${t.spaceLg}px`,
        borderBottom: `1px solid ${t.borderRowDivider}`,
    },
    sectionHeading: {
        margin: 0, marginBottom: t.spaceSm,
        fontSize: t.fontSizeMd, fontWeight: t.fontWeightSemibold,
        color: t.textPrimary,
    },

    // Card surface — used by Response Mapper's group cards.
    card: {
        border: `1px solid ${t.borderLight}`,
        borderRadius: t.radiusLg,
        background: t.panelBg,
        padding: t.spaceMd,
    },
    cardSelected: {
        borderColor: t.primary,
        boxShadow: `0 0 0 1px ${t.primary}`,
    },

    // Footer (counts / pagination).
    footer: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: `${t.spaceSm + 2}px ${t.spaceLg}px`,
        borderTop: `1px solid ${t.borderLight}`,
        background: '#FAFBFC',
        fontSize: t.fontSizeSm, color: t.textSecondary,
    },
};

const _default = { tokens, panelStyles };
export default _default;
